/**
 * TypeScript type definitions for Blood Pressure Tracking feature.
 * Maps to backend Pydantic models in backend/src/models/blood_pressure.py
 */

export type ReadingType = "morning" | "night";

export type BPCategory =
  | "normal"
  | "elevated"
  | "hypertension_stage_1"
  | "hypertension_stage_2"
  | "hypotensive";

/**
 * Input payload for creating/updating a BP reading.
 * Used in POST /api/bp/readings and PATCH /api/bp/readings/:id
 */
export interface BPReadingInput {
  reading_type: ReadingType;
  systolic: number;          // 60-250 mmHg
  diastolic: number;         // 40-150 mmHg
  pulse?: number | null;     // 40-200 bpm (optional)
  notes?: string | null;     // max 500 chars (optional)
}

/**
 * Guidance severity levels for BP readings.
 */
export type GuidanceSeverity = "success" | "info" | "warning" | "alert";

/**
 * BP guidance message returned with readings.
 */
export interface BPGuidance {
  message: string;
  severity: GuidanceSeverity;
}

/**
 * Complete BP reading object returned from API.
 * Matches BPReadingResponse Pydantic model from backend.
 */
export interface BPReading {
  id: string;                // MongoDB _id (mapped from _id to id in response)
  _id?: string;              // MongoDB _id (alternative field name)
  user_id: string;
  date: string;              // ISO date string "YYYY-MM-DD"
  reading_type: ReadingType;
  systolic: number;
  diastolic: number;
  pulse: number | null;
  notes: string | null;
  category: BPCategory;
  guidance?: BPGuidance;     // Educational guidance (included in POST response)
  created_at: string;        // ISO datetime string
  updated_at: string;        // ISO datetime string
}

/**
 * Response from GET /api/bp/readings (list endpoint).
 */
export interface BPReadingsListResponse {
  readings: BPReading[];
  total: number;             // Total count for pagination
  limit: number;
  offset: number;
}

/**
 * Trend data for a single date (can have morning and/or night readings).
 */
export interface BPTrendDayData {
  date: string;              // "YYYY-MM-DD"
  morning?: {
    systolic: number;
    diastolic: number;
    pulse: number | null;
    category: BPCategory;
  };
  night?: {
    systolic: number;
    diastolic: number;
    pulse: number | null;
    category: BPCategory;
  };
}

/**
 * Statistics calculated from BP readings over a date range.
 */
export interface BPStatistics {
  avg_systolic: number;
  avg_diastolic: number;
  avg_pulse: number | null;  // null if no readings have pulse data
  min_systolic: number;
  max_systolic: number;
  min_diastolic: number;
  max_diastolic: number;
  total_readings: number;
  date_range: {
    start: string;           // "YYYY-MM-DD"
    end: string;             // "YYYY-MM-DD"
  };
}

/**
 * Distribution of readings across BP categories.
 */
export interface BPCategoryDistribution {
  normal: number;
  elevated: number;
  hypertension_stage_1: number;
  hypertension_stage_2: number;
  hypotensive: number;
}

/**
 * Response from GET /api/bp/trends.
 * Returns trend data, statistics, and category distribution.
 */
export interface BPTrendsResponse {
  trend_data: BPTrendDayData[];
  statistics: BPStatistics | null;           // null if insufficient data (< 3 readings)
  category_distribution: BPCategoryDistribution | null;  // null if insufficient data
  message?: string;                          // e.g., "Not enough data for trend analysis"
}

/**
 * Query parameters for GET /api/bp/readings.
 */
export interface BPReadingsQueryParams {
  days?: number;             // Filter by last N days (default: 30)
  reading_type?: ReadingType;  // Filter by morning or night
  category?: BPCategory;     // Filter by category
  limit?: number;            // Max results (default: 100)
  offset?: number;           // Pagination offset (default: 0)
}

/**
 * User-friendly category labels for display.
 */
export const BP_CATEGORY_LABELS: Record<BPCategory, string> = {
  normal: "Normal",
  elevated: "Elevated",
  hypertension_stage_1: "Hypertension Stage 1",
  hypertension_stage_2: "Hypertension Stage 2",
  hypotensive: "Low Blood Pressure"
};

/**
 * Color scheme for BP categories (matches BOSI brand palette).
 */
export const BP_CATEGORY_COLORS: Record<BPCategory, string> = {
  normal: "#22c55e",           // Green
  elevated: "#eab308",         // Yellow
  hypertension_stage_1: "#f97316",  // Orange
  hypertension_stage_2: "#ef4444",  // Red
  hypotensive: "#3b82f6"       // Blue
};

/**
 * Reading type labels for display.
 */
export const READING_TYPE_LABELS: Record<ReadingType, string> = {
  morning: "Morning",
  night: "Night"
};

/**
 * Reading type icons (emoji).
 */
export const READING_TYPE_ICONS: Record<ReadingType, string> = {
  morning: "☀️",
  night: "🌙"
};
